import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function OAuthCallbackPage() {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');

        // Check for OAuth errors
        if (errorParam) {
          setError(`Autenticazione annullata: ${searchParams.get('error_description') || errorParam}`);
          setStatus('error');
          return;
        }

        if (!code) {
          setError('Codice di autorizzazione mancante');
          setStatus('error');
          return;
        }

        // Verify state for CSRF protection
        const savedState = sessionStorage.getItem('oauth_state');
        if (savedState && state !== savedState) {
          setError('Errore di sicurezza: state mismatch');
          setStatus('error');
          return;
        }
        sessionStorage.removeItem('oauth_state');

        setStatus('exchanging');

        // Exchange code for user info via our backend
        const redirectUri = `${window.location.origin}/auth/callback/${provider}`;
        const response = await fetch(`${API_URL}/api/auth/oauth/${provider}/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            state,
            redirect_uri: redirectUri
          })
        });

        const result = await response.json();

        if (!result.success) {
          setError(result.error || 'Errore durante l\'autenticazione');
          setStatus('error');
          return;
        }

        setStatus('authenticating');

        // Get user info from OAuth provider
        const oauthUser = result.user;

        if (!oauthUser.email) {
          setError('Email non disponibile dal provider. Assicurati di autorizzare l\'accesso all\'email.');
          setStatus('error');
          return;
        }

        // Check if backend says profile is complete
        if (result.profile_complete && !result.is_first_oauth_login) {
          // User has a complete profile AND has already chosen their role - auto-login
          toast.success(`Bentornato! Accesso in corso...`);
          
          if (result.access_token && result.refresh_token) {
            // Set the session directly using the tokens from backend
            try {
              const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: result.access_token,
                refresh_token: result.refresh_token
              });
              
              if (!sessionError && sessionData?.session) {
                console.log('Session set successfully');
                await refreshUser();
                // Redirect to home page instead of dashboard
                navigate('/');
                return;
              } else {
                console.log('Session set failed:', sessionError);
              }
            } catch (e) {
              console.log('Session set error:', e);
            }
          }
          
          // Fallback: la sessione non è stata impostata dal callback, ma il backend
          // ha confermato che il profilo è completo. Redirect silenzioso — se
          // l'utente non è realmente autenticato, il middleware lato pagina lo
          // devierà su /login senza toast fuorvianti.
          navigate('/');
          return;
        }
        
        // First OAuth login OR incomplete profile - redirect to signup for role selection
        if (result.is_first_oauth_login) {
          // User has profile but hasn't chosen role yet
          setStatus('redirecting');
          toast.success('Benvenuto! Scegli come vuoi usare Rarifex.');
        } else {
          // New user - no profile yet
          setStatus('redirecting');
          toast.success('Account verificato! Completa la registrazione.');
        }
        navigate('/signup', { 
          state: { 
            oauthUser: {
              email: oauthUser.email,
              name: oauthUser.name,
              firstName: oauthUser.given_name,
              lastName: oauthUser.family_name,
              picture: oauthUser.picture,
              provider: provider,
              providerId: oauthUser.provider_id
            }
          } 
        });

      } catch (err) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Errore durante l\'autenticazione');
        setStatus('error');
      }
    };

    handleCallback();
  }, [provider, searchParams, navigate, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="oauth-callback-page">
      <div className="bg-white rounded-lg border border-slate-200 shadow-lg p-8 max-w-md w-full text-center">
        {status === 'error' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Autenticazione Fallita</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/accedi')}
              className="px-6 py-2 bg-[#008080] text-white rounded-lg hover:bg-[#006666] transition-colors"
            >
              Torna al Login
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-[#008080] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              {status === 'processing' && 'Verifica in corso...'}
              {status === 'exchanging' && 'Recupero dati profilo...'}
              {status === 'authenticating' && 'Autenticazione...'}
              {status === 'redirecting' && 'Reindirizzamento...'}
            </h2>
            <p className="text-slate-600">
              Stiamo verificando la tua identità con {provider === 'google' ? 'Google' : 'Facebook'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
